import { z } from 'zod';
import { ALERT_RULE_TYPES, ALERT_SEVERITIES, type AlertRuleType, type AlertSeverity } from './alert-rules.schemas';
import { defineListQuery, listFilter, queryFlag } from './list-query.schemas';

/** Raised alerts (gitaumoses4/chukua#94). The status is derived from the alert's transitions, never stored. */

export const ALERT_STATUSES = ['open', 'acknowledged', 'resolved'] as const;
export type AlertStatus = (typeof ALERT_STATUSES)[number];

export const alertTransitionInputSchema = z.object({ note: z.string().trim().max(500).optional() });
export type AlertTransitionInput = z.output<typeof alertTransitionInputSchema>;

/** Resolving closes the alert for good, so the person says what they did (gitaumoses4/chukua#95). Acknowledging stays optional. */
export const alertResolveInputSchema = z.object({ note: z.string().trim().min(1, 'Say what was done').max(500) });
export type AlertResolveInput = z.output<typeof alertResolveInputSchema>;

/** `GET /alerts` — search matches the truck's registration or the rule's name as it was when the alert fired. */
export const alertListQuerySchema = defineListQuery({
  sortFields: ['raisedAt', 'severity'],
  defaultSort: { sort: 'raisedAt', order: 'desc' },
  filters: {
    status: listFilter(z.enum(ALERT_STATUSES)),
    severity: listFilter(z.enum(ALERT_SEVERITIES)),
    type: listFilter(z.enum(ALERT_RULE_TYPES)),
    truckId: listFilter(z.uuid()),
    ruleId: listFilter(z.uuid()),
    /** `mine=true`: the caller is the alert's current owner (resolved server-side from the session). Only `true` narrows the list; `false` is the same as leaving it off. */
    mine: queryFlag,
    /** `overdue=true`: still open past its severity's escalation threshold. Only `true` narrows the list. */
    overdue: queryFlag,
  },
});
export type AlertListQuery = z.infer<typeof alertListQuerySchema>;

/** `POST /alerts/:id/reassign`: hands the alert to someone who can answer it. The note is optional. */
export const alertReassignInputSchema = z.object({ assigneeId: z.uuid(), note: z.string().trim().max(500).optional() });
export type AlertReassignInput = z.output<typeof alertReassignInputSchema>;

/** What `GET /alerts/stream` sends: that an alert changed, and how. No alert content: each browser refetches its own filtered list. */
export const ALERT_FEED_EVENT_KINDS = ['raised', 'escalated', 'acknowledged', 'resolved', 'reassigned'] as const;
export interface AlertFeedEvent {
  alertId: string;
  kind: (typeof ALERT_FEED_EVENT_KINDS)[number];
}

/** A person an alert can be handed to: holds `alert.respond`. */
export interface AlertAssignee {
  id: string;
  name: string;
}

/** The trip a truck is on now, so the inbox can open it; alerts themselves are about a truck only. */
export interface AlertCurrentTrip {
  id: string;
  number: string;
}

export interface AlertView {
  id: string;
  ruleId: string;
  ruleName: string;
  type: AlertRuleType;
  severity: AlertSeverity;
  truckId: string;
  truckRegistration: string;
  /** The trip the truck is on (past `assigned`, not yet delivered), if any: where the inbox drills down to. */
  currentTrip: AlertCurrentTrip | null;
  /** Who the alert was last handed to; null when nobody was. Separate from who acknowledged it. */
  ownerId: string | null;
  ownerName: string | null;
  status: AlertStatus;
  /** One line saying what happened, built from what the evaluator saw. */
  summary: string;
  raisedAt: string;
  acknowledgedAt: string | null;
  /** The person who acknowledged it; null while open. */
  acknowledgedByName: string | null;
  /** When it went unanswered past its severity's threshold and was handed to the escalation roles; null if it never was. */
  escalatedAt: string | null;
  resolvedAt: string | null;
  /** The person who resolved it; null while unresolved and when the system closed it. */
  resolvedByName: string | null;
  /** Null while unresolved; `'system'` when the condition cleared on its own. */
  resolvedBy: 'system' | 'user' | null;
  resolutionNote: string | null;
}
